import React, { FunctionComponent, ReactElement, ReactNode, useRef, useState } from 'react';
import styled from 'styled-components';
import ContentWrapper from './ContentWrapper';
import MobileMenuButton from './svg/MobileMenuButton';
import TabNavigationItem, { TabTitleProp } from './TabNavigationItem';
import { useQueryParam, withDefault } from 'use-query-params';
import { SelectedTabParam } from './pages/My';

interface TabsProps {
    children: Array<ReactElement<{ title: ReactNode }> | null>;
}

const TabNavigation: FunctionComponent<React.PropsWithChildren<TabsProps>> = ({ children }) => {
    const [selectedTab, setSelectedTab] = useQueryParam('selectedTab', withDefault(SelectedTabParam, 'dashboard'));
    const [showLeftButton, setShowLeftButton] = useState(false);
    const [showRightButton, setShowRightButton] = useState(true);
    const ref = useRef<HTMLUListElement | null>(null);

    const slideRight = () => {
        const scrollWidth = ref.current?.scrollWidth ?? 0;
        ref.current?.scrollTo({ left: scrollWidth, behavior: 'smooth' });
    };

    const slideLeft = () => {
        ref.current?.scrollTo({ left: 0, behavior: 'smooth' });
    };

    const handleScroll = () => {
        const scrollLeft = ref.current?.scrollLeft ?? 0;
        const clientWidth = ref.current?.clientWidth ?? 0;
        const scrollWidth = ref.current?.scrollWidth ?? 0;

        setShowLeftButton(scrollLeft > 0);
        setShowRightButton(scrollLeft + clientWidth < scrollWidth);
    };

    return (
        <>
            <MainWrapper>
                <TabsWrapper>
                    {/* Needs Gradient background */}
                    {showLeftButton && (
                        <LeftMobileControllButtonWrapper onClick={() => slideLeft()}>
                            <LeftButton />
                        </LeftMobileControllButtonWrapper>
                    )}
                    {showRightButton && (
                        <MobileControllButtonWrapper onClick={() => slideRight()}>
                            <MobileMenuButton />
                        </MobileControllButtonWrapper>
                    )}
                    <TabsList ref={ref} onScroll={handleScroll}>
                        {children
                            .filter((child): child is ReactElement<TabTitleProp> => !!child)
                            .map((item, index) => (
                                <TabNavigationItem
                                    key={index}
                                    title={item.props.title}
                                    slug={item.props.slug}
                                    isSelected={item.props.slug === selectedTab}
                                    setSelectedTab={() => setSelectedTab(item.props.slug)}
                                />
                            ))}
                    </TabsList>
                </TabsWrapper>
            </MainWrapper>
            <TabContentWrapper>
                <TabContent>
                    {children
                        .filter((child): child is ReactElement<TabTitleProp> => !!child)
                        .map(child => (
                            <div
                                key={child.props.slug}
                                style={{ display: child.props.slug === selectedTab ? 'block' : 'none' }}
                            >
                                {child}
                            </div>
                        ))}
                </TabContent>
            </TabContentWrapper>
        </>
    );
};

export default TabNavigation;

const MainWrapper = styled.div`
    background-color: ${({ theme }) => theme.colors.neutral['5']};
`;

const TabsList = styled.ul`
    padding: 0;
    margin: 0;
    display: flex;
    background-color: ${({ theme }) => theme.colors.neutral['5']};

    @media screen and (max-width: ${({ theme }) => theme.mediaQueries.s}) {
        overflow: auto;
        white-space: nowrap;
        overflow-x: scroll;
        scroll-behavior: smooth;
        &::-webkit-scrollbar {
            background: transparent;
            -webkit-appearance: none;
            width: 0;
            height: 0;
            margin-top: 0.5rem;
        }
    }
`;

const TabsWrapper = styled(ContentWrapper)`
    margin-bottom: 0;
`;

const TabContentWrapper = styled.div`
    display: flex;
    justify-content: center;
`;

const TabContent = styled(ContentWrapper)`
    width: 100%;
`;

const MobileControllButtonWrapper = styled.div`
    position: absolute;
    right: 0.6rem;
    cursor: pointer;

    //this hardcoded size has a reason so the component can show on different language words size
    @media screen and (min-width: 500px) {
        display: none;
    }
`;

const LeftMobileControllButtonWrapper = styled.div`
    position: absolute;
    left: 0.6rem;
    cursor: pointer;

    //this hardcoded size has a reason so the component can show on different language words size
    @media screen and (min-width: 500px) {
        display: none;
    }
`;

const LeftButton = styled(MobileMenuButton)`
    transform: scaleX(-1);
`;
